import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const API = "/api/auditoria/tipo-documento";

const authConfig = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  },
});

const Auditoria_ListarTipoDocumento = () => {
  const navigate = useNavigate();

  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buscar, setBuscar] = useState("");
  const [estado, setEstado] = useState("");

  const manejarError = useCallback(
    (error, mensaje) => {
      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        Swal.fire("Sesión expirada", "Inicia sesión nuevamente.", "warning").then(
          () => navigate("/login")
        );
        return;
      }

      Swal.fire(
        "Error",
        error?.response?.data?.message || mensaje || "Ocurrió un error.",
        "error"
      );
    },
    [navigate]
  );

  const cargar = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await axios.get(API, authConfig());

      setRegistros(
        Array.isArray(data?.tiposDocumento) ? data.tiposDocumento : []
      );
    } catch (error) {
      manejarError(error, "No se pudieron cargar los tipos de documento.");
      setRegistros([]);
    } finally {
      setLoading(false);
    }
  }, [manejarError]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const datosFiltrados = useMemo(() => {
    const texto = buscar.trim().toLowerCase();

    return registros.filter((item) => {
      const coincideTexto =
        !texto ||
        [
          item.nombreTipoDocumento,
          item.codigoTipoDocumento,
          item.moduloDocumento,
          item.modeloDocumento,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(texto);

      const coincideEstado =
        !estado || item.estadoTipoDocumento === estado;

      return coincideTexto && coincideEstado;
    });
  }, [registros, buscar, estado]);

  const eliminar = async (registro) => {
    const confirmacion = await Swal.fire({
      title: "¿Eliminar tipo de documento?",
      text: registro.nombreTipoDocumento,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (!confirmacion.isConfirmed) return;

    try {
      await axios.delete(`${API}/${registro._id}`, authConfig());
      await cargar();
      Swal.fire("Correcto", "Registro eliminado.", "success");
    } catch (error) {
      manejarError(error, "No se pudo eliminar el registro.");
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h3 className="mb-1">Tipos de documento auditables</h3>
          <p className="text-muted mb-0">
            Catálogo de documentos que pueden ingresar al proceso de auditoría.
          </p>
        </div>

        <button
          className="btn btn-primary"
          onClick={() => navigate("/auditoria/tipo-documento/nuevo")}
        >
          Registrar tipo de documento
        </button>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="row g-2 mb-3">
            <div className="col-md-8">
              <input
                className="form-control"
                placeholder="Buscar por nombre, código, módulo o modelo..."
                value={buscar}
                onChange={(e) => setBuscar(e.target.value)}
              />
            </div>

            <div className="col-md-4">
              <select
                className="form-select"
                value={estado}
                onChange={(e) => setEstado(e.target.value)}
              >
                <option value="">Todos los estados</option>
                <option value="Activo">Activo</option>
                <option value="Inactivo">Inactivo</option>
              </select>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead>
                <tr>
                  <th>Documento</th>
                  <th>Código</th>
                  <th>Módulo</th>
                  <th>Modelo MongoDB</th>
                  <th>Auditable</th>
                  <th>Estado</th>
                  <th className="text-end">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      Cargando...
                    </td>
                  </tr>
                ) : datosFiltrados.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4 text-muted">
                      No hay registros para mostrar.
                    </td>
                  </tr>
                ) : (
                  datosFiltrados.map((item) => (
                    <tr key={item._id}>
                      <td>{item.nombreTipoDocumento || "—"}</td>
                      <td>{item.codigoTipoDocumento || "—"}</td>
                      <td>{item.moduloDocumento || "—"}</td>
                      <td>
                        <code>{item.modeloDocumento || "—"}</code>
                      </td>
                      <td>{item.permiteAuditoria ? "Sí" : "No"}</td>
                      <td>{item.estadoTipoDocumento || "—"}</td>
                      <td className="text-end">
                        <div className="btn-group">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() =>
                              navigate(
                                `/auditoria/tipo-documento/editar/${item._id}`
                              )
                            }
                          >
                            Editar
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => eliminar(item)}
                          >
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auditoria_ListarTipoDocumento;
